import {
  Button,
  DatePicker,
  Dropdown,
  formatNumber,
  FormikSubmit,
  FullPageLoad,
  getDateWithHumanReadableTime,
  ItemSplitter,
  OverflowContainer,
  RowFlex,
  sortAndFormatOptions,
  TextField,
  Toggle,
  useAxios,
  useToast,
} from "common";
import { Field, Formik } from "formik";
import { omit, orderBy } from "lodash";
import { useState } from "react";
import { eBrandGoalType } from "../static";
import AddGoals from "./AddGoals";
import { formikSchema } from "./formikSchema";

const AddGoal = ({ close, onCompleted, goal, thisDate, currentGoals = [] }) => {
  const { alertSuccess, onError } = useToast();
  const { response } = useAxios({
    callOnLoad: {
      method: "GET",
      url: "/brands",
      params: {
        limit: 100000,
        select: "name purchaseOrders saleOrders",
        populate: JSON.stringify([
          {
            path: "saleOrders",
            select: ["OrderNumber", "Amount", "OrderDate"],
          },
          {
            path: "purchaseOrders",
            select: ["Status", "Amount", "PODate", "PONumber"],
          },
        ]),
      },
    },
  });

  const { callAxios: getSos } = useAxios({});
  const [allSos, setAllSos] = useState([]);
  const allSoOptions = sortAndFormatOptions({
    list: allSos,
    valueKey: "id",
    labelKey: (v) => `${v.OrderNumber} ($${formatNumber(v.Amount)})`,
  });

  const { callAxios, loading } = useAxios();

  const doMany = async (goals) => {
    await Promise.all(
      goals.map((data) =>
        callAxios({
          method: "POST",
          url: `/brandGoals`,
          data: omit(data, ["id"]),
        })
      )
    );
  };

  if (!response) return <FullPageLoad fillWidth />;

  const currentBrandIds = currentGoals
    .map((c) => c.brand?.id ?? "")
    .filter(Boolean);
  const allBrands = response.data.data.filter(
    (d) => !currentBrandIds.includes(d.id)
  );

  const brandOptions = sortAndFormatOptions({
    list: allBrands,
    labelKey: (b) => {
      const posByDate = orderBy(
        b.purchaseOrders,
        (p) => new Date(p.PODate),
        "desc"
      );
      const latestPo = posByDate[0];
      const latestDate = getDateWithHumanReadableTime(latestPo?.PODate);
      const latestAmount = formatNumber(latestPo?.Amount);
      const latest = latestPo ? ` (${latestDate} - $${latestAmount})` : "";
      return `${b.name}${latest}`;
    },
    valueKey: "id",
  });

  if (!goal)
    return (
      <AddGoals
        loading={loading}
        close={close}
        brands={allBrands}
        thisDate={thisDate}
        doMany={doMany}
        onCompleted={onCompleted}
      />
    );

  return (
    <Formik
      {...formikSchema(goal, thisDate)}
      onSubmit={(data) => {
        if (goal)
          return callAxios({
            method: "PUT",
            url: `/brandGoals/${goal.id}`,
            data: {
              ...omit(data, ["isNew"]),
              type: data.isNew ? eBrandGoalType.NEW : goal.type,
            },
          })
            .then(() => {
              alertSuccess("Goal Modified");
              onCompleted();
            })
            .catch(onError);

        doMany(data.brands)
          .then(() => {
            alertSuccess("Goal Modified");
            onCompleted();
          })
          .catch(onError);
      }}
    >
      {({ values }) => {
        const loadingSos = values.openToAllSos && allSos.length === 0;
        if (loadingSos) {
          getSos({
            method: "GET",
            url: "/saleOrders",
            params: {
              limit: 100000,
              select: "OrderNumber Amount OrderDate",
            },
          }).then((d) => setAllSos(d.data.data));
        }
        const goalBrand = allBrands.find((btu) => btu.id === values.brand);
        const brandSosToUse = sortAndFormatOptions({
          list: goalBrand?.saleOrders ?? [],
          valueKey: "id",
          labelKey: (v) => `${v.OrderNumber} ($${formatNumber(v.Amount)})`,
        });
        return (
          <OverflowContainer titleWithBodyAndActions>
            <h3>Edit Goal</h3>
            <div>
              <ItemSplitter>
                <Field
                  name="brand"
                  label="Brand"
                  required
                  component={Dropdown}
                  options={brandOptions}
                />
                <Field
                  name="month"
                  component={DatePicker}
                  label="Month"
                  required
                  showMonthYearPicker
                />
                <Field
                  name="goalAmount"
                  component={TextField}
                  type="number"
                  label="Goal Amount"
                  prefix="$"
                  required
                />
                {loadingSos ? (
                  <div>Loading Sos...</div>
                ) : (
                  <Field
                    label="Custom SOs"
                    disabled={!values.brand}
                    name="customSos"
                    component={Dropdown}
                    isMulti
                    options={values.openToAllSos ? allSoOptions : brandSosToUse}
                  />
                )}
                <RowFlex>
                  <Field name="repeat" component={Toggle} label="Repeat" />
                  <Field
                    name="openToAllSos"
                    component={Toggle}
                    label="All SOs"
                  />
                  {goal.type !== eBrandGoalType.NEW && (
                    <Field name="isNew" component={Toggle} label="Make New" />
                  )}
                </RowFlex>
                <Field
                  label="SOs to Exclude"
                  disabled={!values.brand}
                  name="sosToExclude"
                  component={Dropdown}
                  isMulti
                  options={sortAndFormatOptions({
                    list: goal?.soExcludeOptions ?? [],
                    valueKey: "id",
                    labelKey: (v) =>
                      `${v.OrderNumber} ($${formatNumber(v.Amount)})`,
                  })}
                />
                <Field
                  name="notes"
                  component={TextField}
                  isArea
                  label="Notes"
                />
              </ItemSplitter>
            </div>
            <ItemSplitter>
              <Button secondary onClick={close}>
                Cancel
              </Button>
              <FormikSubmit loading={loading}>Edit</FormikSubmit>
            </ItemSplitter>
          </OverflowContainer>
        );
      }}
    </Formik>
  );
};

export default AddGoal;
